import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal, useFloatBtn } from '../components/hooks';
import Footer from '../components/Footer';
import './FAQ.css';

const faqCategories = [
  {
    cat: 'General',
    icon: '💬',
    items: [
      { q: 'Do I need a gym?', a: 'Absolutely not. Your plan is built around whatever equipment you have — full gym, home setup, resistance bands, or bodyweight only. Both options are fully covered in every package.' },
      { q: 'Can I do this if I\'m a complete beginner?', a: 'Yes — Liam coaches all experience levels, from first-timers to experienced lifters who\'ve hit a plateau. The plan meets you exactly where you are right now. No experience required.' },
      { q: 'What if I\'m not local — can I still join?', a: '100%. The full coaching experience — training, nutrition, check-ins, and support — is available entirely online. Location is never a barrier. Liam coaches clients in over 50 countries.' },
      { q: 'How quickly will I see results?', a: 'Most clients notice measurable changes within 3–4 weeks. Visible, photograph-worthy transformation typically builds from week 6 onward. Consistent effort = consistent results.' },
      { q: 'Is Liam W Coaching suitable for women?', a: 'Absolutely. Liam coaches both men and women and tailors every programme accordingly. He\'s helped hundreds of women lose fat, build lean muscle, and completely transform how they feel — without ever making them feel intimidated or pushed too hard.' },
    ]
  },
  {
    cat: 'Packages',
    icon: '📦',
    items: [
      { q: 'What\'s the difference between IGNITE and FORGE?', a: 'IGNITE gives you a premium custom programme and nutrition plan with weekly check-ins via the client portal. FORGE adds direct WhatsApp access to Liam, weekly video calls, deeper tracking, mindset coaching, and in-person session discounts if you\'re local.' },
      { q: 'Can I upgrade from IGNITE to FORGE?', a: 'Absolutely. Many clients start with IGNITE and upgrade once they\'re ready for the full experience. Just message Liam via the client portal and the upgrade is processed within 24 hours.' },
      { q: 'How do payments work?', a: 'Both packages are monthly rolling subscriptions. You\'re billed on the same date each month and can cancel any time with no penalties. Annual billing (20% off) is billed once upfront.' },
      { q: 'Is there a money-back guarantee?', a: 'Yes — both packages include a full refund within 14 days, no questions asked. On FORGE, if you follow the programme 100% and don\'t see results, Liam will continue coaching you free of charge until you do.' },
      { q: 'Can I cancel anytime?', a: 'Yes — both packages are rolling monthly subscriptions. No lock-in contracts, no cancellation fees. Stay for as long as you\'re getting results — which, if you put in the work, will be a long time.' },
    ]
  },
  {
    cat: 'Training',
    icon: '🏋️',
    items: [
      { q: 'What if I don\'t have much equipment?', a: 'No problem. Liam builds programmes around what you actually have. Whether that\'s a full commercial gym, a set of dumbbells at home, or just bodyweight — the programme is built specifically for your setup.' },
      { q: 'How many days a week do I need to train?', a: 'Liam works around your lifestyle. Most programmes are 3–5 days per week, but if you can only commit to 3 days, that\'s exactly what you\'ll get — a programme that maximises your time and gets results within that constraint.' },
      { q: 'Do you offer in-person sessions?', a: 'In-person sessions are available for local clients. FORGE clients receive a discounted rate per session. If you\'re not local, the full online experience delivers the exact same results. All check-ins and coaching still happen live via video call.' },
      { q: 'How are programmes adjusted over time?', a: 'IGNITE programmes are updated every 4–6 weeks based on your progress check-ins. FORGE programmes are adjusted every 3–4 weeks, with real-time tweaks possible via WhatsApp based on how you\'re feeling and progressing week to week.' },
    ]
  },
  {
    cat: 'Nutrition',
    icon: '🥗',
    items: [
      { q: 'Do I have to follow a strict meal plan?', a: 'No — Liam uses a flexible nutrition approach built around your food preferences, lifestyle, and schedule. You\'ll get calorie and macro targets alongside a strategy that works in the real world — not a rigid plan you can\'t stick to.' },
      { q: 'I have dietary restrictions — is that okay?', a: 'Absolutely. Your nutrition plan is built around your food preferences, allergies, and dietary requirements. Whether you\'re vegetarian, vegan, dairy-free, or have other restrictions, the plan accommodates you completely.' },
      { q: 'Will I have to give up my favourite foods?', a: 'No. Liam\'s approach is built around sustainability — which means flexibility. Restricting every food you love leads to failure. The plan is built so that life can still happen while you transform your body.' },
      { q: 'Does the programme include supplements?', a: 'Yes — both packages include supplement guidance and recommendations. You\'re never required to buy anything, but Liam will advise on what\'s worth taking for your goals and what to avoid.' },
    ]
  },
  {
    cat: 'Medical & Safety',
    icon: '🏥',
    items: [
      { q: 'What if I have a medical condition or injury?', a: 'Please mention any medical conditions or injuries in your application. Liam will build your programme around your specific situation. He has coached clients recovering from surgeries, managing chronic conditions, and more.' },
      { q: 'I\'m older — is this suitable for me?', a: 'Definitely. Liam coaches clients of all ages. Some of his most dramatic transformations have come from clients in their 40s, 50s, and 60s. The programme is fully adapted to your age, recovery capacity, and any physical limitations.' },
      { q: 'I\'ve never exercised before — is this safe?', a: 'Completely safe. Every programme is built from scratch with your starting point in mind. For complete beginners, Liam starts conservatively to build proper form, movement patterns, and habits before increasing intensity.' },
    ]
  },
];

function FaqItem({ q, a, isOpen, onToggle }) {
  return (
    <div className={`faq-item${isOpen ? ' open' : ''}`}>
      <div className="faq-q" onClick={onToggle}>
        {q} <span className="faq-ico">+</span>
      </div>
      <div className="faq-a"><p>{a}</p></div>
    </div>
  );
}

export default function FAQ() {
  useReveal();
  useFloatBtn();
  const [openMap, setOpenMap] = useState({});
  const [activecat, setActiveCat] = useState('all');

  const toggle = (key) => setOpenMap(m => ({ ...m, [key]: !m[key] }));

  const displayCats = activecat === 'all'
    ? faqCategories
    : faqCategories.filter(c => c.cat === activecat);

  return (
    <>
      {/* HERO */}
      <section id="phero">
        <div className="phero-bg"></div>
        <div className="phero-grid"></div>
        <div className="phero-tag"><div className="phero-tag-line"></div><span>Everything You Need to Know</span></div>
        <h1 className="phero-title">FREQUENTLY<br /><em>ASKED</em></h1>
        <p className="phero-sub">Got questions before committing? Good. Here are honest answers to everything our clients ask most. Can't find yours? Apply and Liam will answer personally on your free discovery call.</p>
      </section>

      {/* CATEGORY FILTER */}
      <div id="faq-filter">
        <div className="faq-filter-inner">
          <button className={`cat-btn${activecat === 'all' ? ' active' : ''}`} onClick={() => setActiveCat('all')}>All Questions</button>
          {faqCategories.map(c => (
            <button key={c.cat} className={`cat-btn${activecat === c.cat ? ' active' : ''}`} onClick={() => setActiveCat(c.cat)}>
              {c.icon} {c.cat}
            </button>
          ))}
        </div>
      </div>

      {/* FAQ CONTENT */}
      <section id="faq-content">
        {displayCats.map(cat => (
          <div className="faq-cat-section rev" key={cat.cat}>
            <div className="faq-cat-header">
              <span className="faq-cat-icon">{cat.icon}</span>
              <h2 className="faq-cat-title">{cat.cat}</h2>
            </div>
            <div className="faq-items">
              {cat.items.map((item, i) => {
                const key = `${cat.cat}-${i}`;
                return (
                  <FaqItem key={key} q={item.q} a={item.a} isOpen={!!openMap[key]} onToggle={() => toggle(key)} />
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* GUARANTEE */}
      <section id="guarantee">
        <div className="guarantee-inner rev">
          <div className="guarantee-icon">🔒</div>
          <h2 className="s-title">THE GUARANTEE</h2>
          <p>Both packages include a <strong>full 14-day money-back guarantee</strong>, no questions asked. On FORGE, if you follow the programme 100% and don't see results, Liam will continue coaching you <strong>completely free</strong> until you do. Zero risk. All reward.</p>
          <div className="guarantee-btns">
            <Link to="/packages" className="btn-gold">See the Packages →</Link>
            <Link to="/packages" className="btn-outline">Apply Now — It's Free</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta">
        <div className="cta-bg"></div>
        <div className="cta-inner">
          <div className="urgency rev"><div className="udot"></div><span>Limited spots — new intake opens monthly</span></div>
          <div className="s-label rev" style={{ justifyContent: 'center' }}><span>Still Have Questions?</span></div>
          <h2 className="s-title rev">APPLY AND ASK<br /><em style={{ color: 'var(--gold)', fontStyle: 'normal' }}>LIAM DIRECTLY.</em></h2>
          <p className="rev">Every application gets a personal review from Liam. If you have questions, he'll answer them on your free 30-minute discovery call — no commitment required.</p>
          <div className="cta-btns rev">
            <Link to="/packages" className="btn-gold">Apply Now — It's Free</Link>
            <Link to="/packages" className="btn-outline">View Packages →</Link>
          </div>
        </div>
      </section>

      <Footer />
      <Link to="/packages" className="float-btn" id="floatBtn">Apply Now →</Link>
    </>
  );
}
